'use client';

import React, { useState, useContext, useMemo } from 'react';
import { ICartItem } from '@/api/types/cart';
import Image from '@/components/Image';
import Dialog from '@/components/Dialog';
import Popup from '@/components/Popup';
import Tooltip from '@/components/Tooltip';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { EPlatform } from '@/utils/enums/platform-enum';
import { RightArrowOutlined, QuestionOutlined } from '@zeelool/icon';
import logger from '@/utils/client-logger';
import { useTranslations } from '@/i18n/use-translations';
import { IS_DE_FR } from '@/utils/constants';

interface IGiftItem {
  id: number;
  gift_product_qty: number;
  end_time: string;
  gift_product_info?: {
    image?: string;
    color?: string;
    short_name?: string;
  };
  gift_activity_info?: {
    intro?: string;
  };
}

interface IPromotion {
  id: number;
  send_gifts?: IGiftItem[];
}

// 扩展ICartItem类型以包含赠品
interface ICartItemWithGifts extends ICartItem {
  send_gifts?: IGiftItem[];
}

interface GiftsWithCartItemProps {
  cartItemData: ICartItemWithGifts;
  promotion?: IPromotion[];
  className?: string;
}

export default function GiftsWithCartItem({
  cartItemData,
  promotion = [],
  className,
}: GiftsWithCartItemProps) {
  const t = useTranslations('cart.GiftsWithCartItem');
  const { deviceType } = useContext(ResponsiveContext);
  const [showListPop, setShowListPop] = useState(false);
  const currency = useStore((state) => state.currency);

  // 获取当前商品的促销信息
  const itemPromotion = useMemo(() => {
    if (Array.isArray(promotion)) {
      return promotion.find((item) => item.id === cartItemData.cart_id) || {};
    }
    return {};
  }, [promotion, cartItemData.cart_id]);

  // 获取赠品列表
  const giftsList = useMemo(() => {
    if (itemPromotion && Object.keys(itemPromotion).length > 0) {
      const { send_gifts = [] } = itemPromotion as IPromotion;
      return send_gifts;
    }
    return cartItemData?.send_gifts || [];
  }, [itemPromotion, cartItemData]);

  // 计算赠品总数量
  const giftsListQty = useMemo(() => {
    return giftsList.reduce((total, item) => total + Number(item.gift_product_qty || 0), 0);
  }, [giftsList]);

  // 获取赠品图片
  const getGiftImage = (item: IGiftItem) => {
    try {
      return item.gift_product_info?.image || '';
    } catch (error) {
      logger.error('Failed to get gift image:', error);
      return '';
    }
  };

  // 获取赠品信息
  const getGiftProductInfo = (item: IGiftItem) => {
    try {
      const { color = '', short_name = '' } = item.gift_product_info || {};
      return { color, short_name };
    } catch (error) {
      logger.error('Failed to get gift product info:', error);
      return { color: '', short_name: '' };
    }
  };

  // 如果没有赠品，不显示组件
  if (!giftsList.length) {
    return null;
  }

  return (
    <>
      <div className={`rounded-[4px] ${className}`}>
        <div
          className={`
            ${
              deviceType === EPlatform.WAP
                ? 'w-[311px] h-[36px] bg-[#f6f6f6] py-[3px] pl-[3px] pr-[6px] rounded-[6px]'
                : 'h-[56px] bg-[#f8f9fa] py-[4px] pl-[4px] pr-[12px] rounded-[4px]'
            } 
            flex items-center cursor-pointer box-border
          `}
          onClick={() => setShowListPop(true)}
        >
          {/* 赠品图片列表 */}
          <div
            className={`
              ${deviceType === EPlatform.WAP ? 'max-w-[220px] h-[30px]' : 'max-w-[390px] h-[48px]'} 
              overflow-hidden whitespace-nowrap
            `}
          >
            {giftsList.map((item, index) => {
              // 计算间距类名
              const getMarginClass = () => {
                if (index === 0) return '';
                return deviceType === EPlatform.WAP ? 'ml-[4px]' : 'ml-[6px]';
              };

              return (
                <div
                  key={item.id}
                  className={`
                    ${
                      deviceType === EPlatform.WAP
                        ? 'h-[30px] w-[30px] rounded-[3px]'
                        : 'h-[48px] w-[48px] rounded-[4px]'
                    } 
                    ${getMarginClass()} 
                    inline-block bg-white overflow-hidden relative
                  `}
                >
                  <Image
                    src={getGiftImage(item)}
                    alt="gift"
                    width={deviceType === EPlatform.WAP ? 30 : 48}
                    height={deviceType === EPlatform.WAP ? 30 : 48}
                    className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-auto"
                  />
                </div>
              );
            })}
          </div>

          {/* 右箭头 */}
          <RightArrowOutlined
            className={`
              ${deviceType === EPlatform.WAP ? 'ml-[4px] text-[12px]' : 'ml-[6px] text-[14px]'} 
              text-[#a9a9a9]
            `}
          />

          {/* 赠品数量信息 */}
          <div
            className={`ml-auto ${deviceType === EPlatform.WAP ? 'text-[12px]' : 'text-[14px]'} text-[#282828]`}
          >
            <span>{t('gift')}</span>
            {deviceType === EPlatform.WAP && <span className="mx-[4px] ">x</span>}
            {deviceType === EPlatform.WEB && <span>&nbsp;x&nbsp;</span>}
            <span className="font-bb">{giftsListQty}</span>
          </div>
        </div>
      </div>

      {/* PC端弹窗 */}
      {deviceType === EPlatform.WEB && (
        <Dialog
          open={showListPop}
          onClose={() => setShowListPop(false)}
          title={t('giftList')}
          footer={null}
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: '8px',
            },
            '& .MuiDialogContent-root': {
              padding: '0!important',
            },
          }}
        >
          {/* 弹窗内容 */}
          <div className="relative">
            <div className="max-h-[60vh] overflow-y-auto p-[24px] pt-[0px]">
              {giftsList.map((item, index) => {
                const productInfo = getGiftProductInfo(item);
                return (
                  <div
                    key={item.id}
                    className={`flex justify-between h-[128px] ${index > 0 ? 'mt-[24px]' : ''}`}
                  >
                    {/* 左侧图片 */}
                    <div className="h-[128px] w-[128px] rounded-[5px] flex items-center justify-center relative">
                      <Image
                        src={getGiftImage(item)}
                        alt="gift"
                        width={128}
                        height={128}
                        className="block w-[128px] h-[128px] object-cover"
                      />
                    </div>

                    {/* 右侧信息 */}
                    <div className="w-[420px] flex flex-col">
                      {/* Gift标签 */}
                      <span
                        className={`${IS_DE_FR ? 'w-[50px]' : 'w-[32px]'} h-[18px] bg-transparent border border-[#ff3052] text-[#ff3052] text-[12px] text-center leading-[18px] mb-[10px] rounded-[3px] font-br`}
                      >
                        {t('gift')}
                      </span>

                      {/* 标题和价格 */}
                      <div className="flex justify-between items-center font-bs text-[14px]">
                        <span className="max-w-[350px] truncate text-[#282828]">
                          {productInfo.short_name}
                        </span>
                        <span className="text-[#282828] notranslate">
                          {formatPriceNumberWithSymbol('0.00', currency)}
                        </span>
                      </div>

                      {/* 颜色和数量 */}
                      {productInfo.color && (
                        <span className="mt-[3px] text-[14px] text-[#282828]">
                          {t('color')}: {productInfo.color}
                        </span>
                      )}
                      <span className="mt-[3px] text-[14px] text-[#282828]">
                        {t('qty')}: {item.gift_product_qty}
                      </span>

                      {/* 有效期 */}
                      <div className="mt-auto flex items-center text-[#a9a9a9] text-[14px]">
                        <span>
                          {t('valid')}: {item.end_time}
                        </span>
                        {item.gift_activity_info?.intro && (
                          <Tooltip
                            title={item.gift_activity_info.intro}
                            placement="top"
                            variant="light"
                            trigger="hover"
                            sx={{
                              zIndex: 1600,
                              '& .MuiTooltip-tooltip': {
                                maxWidth: '300px',
                                fontSize: '14px',
                                padding: '12px',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'var(--font-bricolage)',
                              },
                            }}
                          >
                            <QuestionOutlined className="ml-[6px] text-[14px] cursor-pointer" />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 底部渐变（当内容超过3个时） */}
            {giftsList.length > 3 && (
              <div className="h-[20px] absolute left-0 bottom-0 w-[100%] bg-gradient-to-t from-white to-transparent rounded-b-[8px]" />
            )}
          </div>
        </Dialog>
      )}

      {/* 移动端弹窗 */}
      {deviceType === EPlatform.WAP && (
        <Popup
          open={showListPop}
          onClose={() => setShowListPop(false)}
          position="bottom"
          title={t('giftList')}
          sx={{
            '& .popup-content': {
              borderRadius: '20px 20px 0 0',
              maxHeight: '60vh',
              minHeight: '38vh',
            },
          }}
        >
          {/* 弹窗内容 */}
          <div className="relative pt-[2px]">
            <div className="">
              {giftsList.map((item, index) => {
                const productInfo = getGiftProductInfo(item);
                return (
                  <div
                    key={item.id}
                    className={`flex justify-between h-[100px] ${index > 0 ? 'mt-[16px]' : ''}`}
                  >
                    {/* 左侧图片 */}
                    <div className="h-[100px] w-[100px] rounded-[8px] flex items-center justify-center relative overflow-hidden">
                      {/* Gift标签 */}
                      <span className="absolute top-0 left-0 bg-[#baacff] px-[7px] text-[10px] h-[15px] leading-[15px] text-center rounded-br-[8px] z-10">
                        {t('gift')}
                      </span>
                      <Image
                        src={getGiftImage(item)}
                        alt="gift"
                        width={100}
                        height={100}
                        className="block w-[100px] h-[100px] object-cover"
                      />
                    </div>

                    {/* 右侧信息 */}
                    <div className="w-[235px] flex flex-col">
                      {/* 标题和价格 */}
                      <div className="flex justify-between items-center font-bs text-[14px]">
                        <span className="max-w-[160px] truncate text-[#282828]">
                          {productInfo.short_name}
                        </span>
                        <span className="text-[#282828] notranslate">
                          {formatPriceNumberWithSymbol('0.00', currency)}
                        </span>
                      </div>

                      {/* 颜色和数量 */}
                      {productInfo.color && (
                        <span className="mt-[7px] text-[12px] text-[#282828]">
                          {t('color')}: {productInfo.color}
                        </span>
                      )}
                      <span className="mt-[7px] text-[12px] text-[#282828]">
                        {t('qty')}: {item.gift_product_qty}
                      </span>

                      {/* 有效期 */}
                      <div className="mt-auto flex items-center text-[#a9a9a9] text-[12px]">
                        <span>
                          {t('valid')}: {item.end_time}
                        </span>
                        {item.gift_activity_info?.intro && (
                          <Tooltip
                            title={item.gift_activity_info.intro}
                            placement="top"
                            variant="light"
                            trigger="click"
                            sx={{
                              zIndex: 1600,
                              '& .MuiTooltip-tooltip': {
                                maxWidth: '300px',
                                fontSize: '14px',
                                padding: '12px',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'var(--font-bricolage)',
                              },
                            }}
                          >
                            <QuestionOutlined className="ml-[6px] text-[12px] cursor-pointer" />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Popup>
      )}
    </>
  );
}
